import React, {useEffect, useState} from "react";
import {
    Box, CircularProgress, Dialog, Grid, IconButton, styled, Table, TableBody, TableCell,
    tableCellClasses, TableContainer, TableHead, TableRow, Chip, Button, ButtonGroup, Alert
} from "@mui/material";
import ErrorMsg from "./msg/ErrorMsg";
import SuccsMsg from "./msg/SuccsMsg";
import Appbar from "./Appbar";
import MenuList from "./MenuList";
import axios from "axios";
import {Navigate} from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import EditUserModal from "./EditUserModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ManageUser = () => {
    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [succsMsg, setSuccsMsg] = useState("");
    const [update, setUpdate] = useState(false);

    // State for edit modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            const token = localStorage.getItem("adminlogintoken");

            if (!token) {
                localStorage.clear();
                return;
            }

            const data = { token };

            try {
                const response = await axios.post("/api/get_userlist.php", data);
                console.log("Response from server:", response.data);

                if (response.status === 200) {
                    if (response.data.error === false) {
                        const users = response.data.data || [];
                        console.log("Users data:", users);

                        // اضافه کردن نقش به صورت دستی برای کاربرانی که role ندارند
                        const enhancedUsers = users.map(user => {
                            // اگر نقش از سرور آمده، از همان استفاده کن
                            if (user.role) {
                                return { ...user, an_role: user.role };
                            }
                            // اگر نقش نیامده، بر اساس id تعیین کن (برای کاربران قدیمی)
                            if (user.id == 1) { // کاربر با id=1 مدیر است
                                return { ...user, an_role: 'admin' };
                            }
                            return { ...user, an_role: 'user' };
                        });

                        setUserList(enhancedUsers);
                    } else {
                        if (response.data.message === "your token has been expired") {
                            localStorage.clear();
                        } else {
                            setErrorMsg(response.data.message || "خطا در دریافت اطلاعات");
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching users:", error);
                setErrorMsg("خطا در ارتباط با سرور");
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
        setUpdate(false);
    }, [update]);

    const Redirect = () => {
        return (<Navigate replace to="/login"/>)
    }

    const handleDeleteUser = async (id) => {
        if (window.confirm("آیا از حذف این کاربر اطمینان دارید؟")) {
            setLoading(true);
            const data = {
                token: localStorage.getItem("adminlogintoken"),
                userid: id,
            }

            try {
                const response = await axios.post("/api/deleteuser.php", data);

                if (response.status === 200) {
                    if (response.data.error === false) {
                        setSuccsMsg(response.data.message || "کاربر با موفقیت حذف شد");
                        setUpdate(true);
                    } else {
                        setErrorMsg(response.data.message || "خطا در حذف کاربر");
                    }
                }
            } catch (error) {
                console.error("Error deleting user:", error);
                setErrorMsg("خطا در ارتباط با سرور");
            } finally {
                setLoading(false);
            }
        }
    }

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setEditModalOpen(true);
    }

    const handleSaveEdit = async (formData) => {
        setLoading(true);
        setEditModalOpen(false);

        const data = {
            token: localStorage.getItem("adminlogintoken"),
            userid: selectedUser.id,
            username: formData.username,
            name: formData.name,
            role: formData.role
        };

        // اگر رمز عبور پر شده بود، اضافه کن
        if (formData.password && formData.password.trim() !== '') {
            data.password = formData.password;
        }

        try {
            const response = await axios.post("/api/edituser.php", data);
            console.log("Edit response:", response.data);

            if (response.status === 200) {
                if (response.data.error === false) {
                    setSuccsMsg(response.data.message || "اطلاعات کاربر با موفقیت به‌روزرسانی شد");
                    setUpdate(true);
                } else {
                    setErrorMsg(response.data.message || "خطا در ویرایش اطلاعات کاربر");
                }
            }
        } catch (error) {
            console.error("Error editing user:", error);
            if (error.response) {
                setErrorMsg(error.response.data?.message || "خطا در ویرایش اطلاعات کاربر");
            } else if (error.request) {
                setErrorMsg("عدم دریافت پاسخ از سرور");
            } else {
                setErrorMsg("خطا در ارتباط با سرور");
            }
        } finally {
            setLoading(false);
        }
    }

    const handleChangeRole = async (id, currentRole) => {
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        const action = newRole === 'admin' ? 'تبدیل به مدیر' : 'تبدیل به کاربر عادی';

        if (window.confirm(`آیا از ${action} این کاربر اطمینان دارید؟`)) {
            setLoading(true);

            const data = {
                token: localStorage.getItem("adminlogintoken"),
                userid: id,
                role: newRole
            };

            try {
                const response = await axios.post("/api/updateuserrole.php", data);
                console.log("Role change response:", response.data);

                if (response.status === 200) {
                    if (response.data.error === false) {
                        // تغییر نقش در فرانت‌اند
                        setUserList(prevList =>
                            prevList.map(user =>
                                user.id === id
                                    ? {...user, role: newRole, an_role: newRole}
                                    : user
                            )
                        );
                        setSuccsMsg(response.data.message || `نقش کاربر با موفقیت به ${newRole === 'admin' ? 'مدیر' : 'کاربر عادی'} تغییر یافت`);
                    } else {
                        setErrorMsg(response.data.message || "خطا در تغییر نقش کاربر");
                    }
                }
            } catch (error) {
                console.error("Error changing role:", error);
                if (error.response) {
                    setErrorMsg(error.response.data?.message || "خطا در تغییر نقش کاربر");
                } else if (error.request) {
                    setErrorMsg("عدم دریافت پاسخ از سرور");
                } else {
                    setErrorMsg("خطا در ارتباط با سرور");
                }
            } finally {
                setLoading(false);
            }
        }
    }

    const getRoleChip = (role) => {
        if (role === 'admin') {
            return (
                <Chip
                    icon={<AdminPanelSettingsIcon />}
                    label="مدیر"
                    color="primary"
                    size="small"
                    sx={{ ml: 1, fontWeight: 'bold' }}
                />
            );
        } else {
            return (
                <Chip
                    icon={<PersonIcon />}
                    label="کاربر عادی"
                    variant="outlined"
                    size="small"
                />
            );
        }
    }

    return (
        <div>
            {localStorage.getItem("adminlogintoken") === "" ? <Redirect/> : ''}
            {localStorage.getItem("adminlogintoken") === null ? <Redirect/> : ''}

            {/* Edit User Modal */}
            <EditUserModal
                open={editModalOpen}
                handleClose={() => setEditModalOpen(false)}
                userData={selectedUser}
                handleSave={handleSaveEdit}
            />

            {/* پیام‌های خطا و موفقیت */}
            {errorMsg && (
                <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ position: 'fixed', top: 10, right: 10, zIndex: 9999 }}>
                    {errorMsg}
                </Alert>
            )}
            {succsMsg && (
                <Alert severity="success" onClose={() => setSuccsMsg("")} sx={{ position: 'fixed', top: 10, right: 10, zIndex: 9999 }}>
                    {succsMsg}
                </Alert>
            )}

            <Grid container spacing={2} sx={{marginTop: 0}}>
                <Grid item xs={12} md={10} sx={{backgroundColor: "#ffffff", minHeight: "100vh"}}>
                    <Appbar/>
                    <Box sx={{padding: 2}} dir={"rtl"}>
                        {/* نمایش آمار کاربران */}
                        {!loading && userList.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip label={`کل کاربران: ${userList.length}`} color="default" />
                                <Chip
                                    label={`مدیران: ${userList.filter(u => u.role === 'admin' || u.an_role === 'admin').length}`}
                                    color="primary"
                                />
                                <Chip
                                    label={`کاربران عادی: ${userList.filter(u => u.role !== 'admin' && u.an_role !== 'admin').length}`}
                                    variant="outlined"
                                />
                            </Box>
                        )}

                        <TableContainer>
                            <Table sx={{minWidth: 250, maxHeight: '80%', overflowY: 'auto'}}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell align={"right"}>
                                            {"نام کاربری"}
                                        </StyledTableCell>
                                        <StyledTableCell align={"right"}>
                                            {"نام و نام خانوادگی"}
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"}>
                                            {"نقش"}
                                        </StyledTableCell>
                                        <StyledTableCell align={"left"}>
                                            {"عملیات"}
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                {loading ? (
                                    <TableBody>
                                        <TableRow>
                                            <StyledTableCell colSpan={4} align="center">
                                                <CircularProgress size={30} />
                                            </StyledTableCell>
                                        </TableRow>
                                    </TableBody>
                                ) : userList.length > 0 ? (
                                    <TableBody>
                                        {userList.map(list => {
                                            const role = list.role || list.an_role || 'user';
                                            return (
                                                <StyledTableRow key={list.id}>
                                                    <StyledTableCell align={"right"}>
                                                        {list.user}
                                                    </StyledTableCell>
                                                    <StyledTableCell align={"right"}>
                                                        {list.name}
                                                    </StyledTableCell>
                                                    <StyledTableCell align={"center"}>
                                                        {getRoleChip(role)}
                                                    </StyledTableCell>
                                                    <StyledTableCell align={"left"}>
                                                        <ButtonGroup variant="text" size="small">
                                                            <Button
                                                                onClick={() => handleChangeRole(list.id, role)}
                                                                color={role === 'admin' ? 'warning' : 'primary'}
                                                                sx={{ minWidth: 'auto' }}
                                                            >
                                                                {role === 'admin' ? 'تبدیل به کاربر' : 'تبدیل به مدیر'}
                                                            </Button>
                                                            <IconButton
                                                                onClick={() => handleEditUser(list)}
                                                                aria-label="edit"
                                                                size="small"
                                                                color="primary"
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => handleDeleteUser(list.id)}
                                                                aria-label="delete"
                                                                size="small"
                                                                color="error"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </ButtonGroup>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            );
                                        })}
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        <TableRow>
                                            <StyledTableCell colSpan={4} align="center">
                                                {"هیچ کاربری یافت نشد"}
                                            </StyledTableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>
                <Grid item xs={0} md={2} sx={{
                    backgroundColor: '#2b2b2b',
                    minHeight: "100vh",
                    display: {xs: "none", md: "block"}
                }}>
                    <MenuList/>
                </Grid>
            </Grid>

            <Dialog open={loading} scroll="paper">
                <CircularProgress sx={{margin: 2}}/>
            </Dialog>
        </div>
    )
}

export default ManageUser;